import { format } from 'date-fns'
import { Link } from 'react-router'
import { SanityImage } from '#app/components/ui/SanityImage.tsx'
import {
	type Event,
	type SanityImageCrop,
	type SanityImageHotspot,
	type Venue,
} from '#app/types/sanity.types.ts'
import { safeFetch } from '#app/utils/sanity.server.ts'
import { type Route } from './+types/index.ts'

type ResolvedVenueThumbnail = {
	asset: {
		_id: string
		metadata?: {
			dimensions: { width: number; height: number; aspectRatio: number }
		}
	}
	hotspot?: SanityImageHotspot
	crop?: SanityImageCrop
	alt?: string
	_type: 'image'
}

interface EventSummary extends Pick<
	Event,
	'_id' | 'displayName' | 'date' | 'dateRange' | 'useDate' | 'eventType'
> {
	locationName: Venue['shortName']
	thumbnail?: ResolvedVenueThumbnail
}

export async function loader({ request: _request }: Route.LoaderArgs) {
	const query = `*[_type == "event"] | order(date desc) {
		_id,
		displayName,
		useDate,
		date,
		dateRange,
		eventType,
		"locationName": location->displayName,
		"thumbnail": location->thumbnail {
			...,
			asset -> {
				_id,
				metadata {
					dimensions {
						width,
						height,
						aspectRatio,
					}
				}
			}
		},
	}`
	const events = await safeFetch<EventSummary[]>(query)

	return { events: events ?? [] }
}

export default function Index({ loaderData }: Route.ComponentProps) {
	const { events } = loaderData
	return (
		<main className="font-poppins w-[calc(100%-12px)] place-items-center px-4">
			<h1 className="text-4xl sm:text-4xl">Event History</h1>
			<p className="mt-2 text-slate-500 dark:text-slate-400">
				Click on an event for details and photos!
			</p>
			<div className="mx-auto max-w-5xl py-12">
				{events.length === 0 ? (
					<p className="text-slate-500 dark:text-slate-400">
						No past events yet. Check back soon!
					</p>
				) : null}
				<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
					{events.map((event) => (
						<Link
							key={event._id}
							to={`/events/${event._id}`}
							className="bg-muted flex w-full items-stretch rounded-xl border border-slate-200 transition-colors hover:border-slate-400 dark:border-slate-700 dark:hover:border-slate-500"
						>
							<div className="flex flex-1 flex-col gap-0.5 p-4">
								<div className="text-scbg-gold text-xl font-bold">
									{event.displayName}
								</div>
								{event.eventType ? (
									<div className="text-sm text-slate-500 dark:text-slate-400">
										{event.eventType.charAt(0).toUpperCase() +
											event.eventType.slice(1)}{' '}
										Backgammon
									</div>
								) : null}
								<div className="text-md mt-1 leading-5 text-slate-700 dark:text-slate-50">
									{event.useDate === 'date'
										? event.date
											? format(new Date(event.date), 'MMM d, yyyy h:mm aaa')
											: ''
										: (event.dateRange ?? '')}
								</div>
								<div className="text-md leading-5 text-slate-700 dark:text-slate-50">
									{event.locationName}
								</div>
							</div>
							{event.thumbnail?.asset && (
								<div className="py-4 pr-4">
									<SanityImage
										source={event.thumbnail}
										alt={event.locationName ?? ''}
										className="h-full w-24 rounded-md object-cover"
									/>
								</div>
							)}
						</Link>
					))}
				</div>
			</div>
		</main>
	)
}
